/**
 * Executive Overview Page
 * Main dashboard with portfolio KPIs and summary metrics
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Briefcase,
  CurrencyEur,
  TrendUp,
  Warning,
  ArrowRight,
  ChartPie,
} from '@phosphor-icons/react';
import { MetricCard, DealCard, LoadingPage, EmptyState } from '../components/common';
import { PageHelpBlock } from '../components/help';
import { pageExplanations } from '../content/helpContent';
import { dealsApi } from '../utils/api';
import { 
  formatCurrency, 
  formatCurrencyCompact, 
  formatPercent,
  getStatusConfig,
} from '../utils/formatters';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

export const ExecutiveOverview = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [deals, setDeals] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [statsData, dealsData] = await Promise.all([
          dealsApi.getStats(),
          dealsApi.getSummaries(),
        ]);
        setStats(statsData);
        setDeals(dealsData);
      } catch (err) {
        console.error('Failed to fetch overview data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) return <LoadingPage message="Chargement de la vue d'ensemble..." />;

  if (error) {
    return (
      <EmptyState
        icon={Warning}
        title="Échec du chargement des données"
        description={error}
      />
    );
  }

  // Filter deals by status for different sections
  const activeDeals = deals.filter(d => 
    !['sold', 'abandoned'].includes(d.status)
  );
  const recentDeals = [...deals]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 4);

  // Calculate active capital (deals not sold/abandoned)
  const activeCapital = activeDeals.reduce((sum, d) => 
    sum + (d.total_project_cost || 0), 0
  );

  return (
    <div data-testid="executive-overview" className="space-y-6 sm:space-y-8 animate-fade-in">
      {/* KPI Grid - Responsive */}
      <section>
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Vue d'Ensemble du Portefeuille</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <MetricCard
            testId="metric-total-deals"
            label="Total Deals"
            value={stats?.total_deals || 0}
            subValue={`${activeDeals.length} actifs`}
            icon={Briefcase}
          />
          <MetricCard
            testId="metric-capital-deployed"
            label="Capital Investi"
            value={formatCurrencyCompact(activeCapital)}
            subValue={formatCurrency(activeCapital)}
            icon={CurrencyEur}
          />
          <MetricCard
            testId="metric-avg-roi"
            label="Rendement Moyen"
            value={formatPercent(stats?.average_roi)}
            icon={TrendUp}
            variant={stats?.average_roi >= 15 ? 'success' : 'default'}
          />
          <MetricCard
            testId="metric-deals-at-risk"
            label="Deals à Risque"
            value={stats?.deals_at_risk || 0}
            icon={Warning}
            variant={stats?.deals_at_risk > 0 ? 'warning' : 'default'}
          />
        </div>
      </section>

      {/* Status Breakdown - Responsive Grid */}
      <section>
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Statut du Pipeline</h2>
        <div className="card-premium p-3 sm:p-6">
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4">
            {stats?.deals_by_status && Object.entries(stats.deals_by_status).map(([status, count]) => {
              const config = getStatusConfig(status);
              return (
                <div key={status} className="text-center p-2 sm:p-4 rounded-lg bg-secondary/30">
                  <Badge 
                    variant="secondary" 
                    className={`${config.color} mb-1 sm:mb-2 text-[10px] sm:text-xs`}
                  >
                    {config.label}
                  </Badge>
                  <p className="text-lg sm:text-2xl font-bold font-tabular">{count}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Expected Profit Summary - Responsive */}
      <section>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <div className="card-premium p-3 sm:p-6 bg-emerald-50/30 border-emerald-200/50">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <TrendUp size={18} className="text-emerald-600 sm:hidden" />
                <TrendUp size={20} className="text-emerald-600 hidden sm:block" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                Profit Attendu Total
              </span>
            </div>
            <p className="text-xl sm:text-3xl font-bold text-emerald-700 font-tabular">
              {formatCurrencyCompact(stats?.total_expected_profit)}
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 truncate">
              {formatCurrency(stats?.total_expected_profit)}
            </p>
          </div>

          <div className="card-premium p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                <ChartPie size={18} className="text-muted-foreground sm:hidden" />
                <ChartPie size={20} className="text-muted-foreground hidden sm:block" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                Profit par Deal (Moy.)
              </span>
            </div>
            <p className="text-xl sm:text-3xl font-bold font-tabular">
              {stats?.total_deals > 0 
                ? formatCurrencyCompact(stats.total_expected_profit / stats.total_deals)
                : '—'}
            </p>
          </div>

          <div className="card-premium p-3 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                <CurrencyEur size={18} className="text-muted-foreground sm:hidden" />
                <CurrencyEur size={20} className="text-muted-foreground hidden sm:block" />
              </div>
              <span className="text-xs sm:text-sm font-medium text-muted-foreground">
                Coût au m² (Moy.)
              </span>
            </div>
            <p className="text-xl sm:text-3xl font-bold font-tabular">
              {deals.length > 0 
                ? formatCurrency(
                    deals.reduce((sum, d) => sum + (d.total_project_cost || 0), 0) /
                    deals.reduce((sum, d) => sum + (d.surface_sqm || 1), 0)
                  ) + '/m²'
                : '—'}
            </p>
          </div>
        </div>
      </section>

      {/* Recent Deals - Responsive Grid */}
      <section>
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <h2 className="text-base sm:text-lg font-semibold">Deals Récents</h2>
          <Link to="/pipeline">
            <Button variant="ghost" size="sm" data-testid="view-all-deals-btn" className="text-xs sm:text-sm">
              Voir Tout
              <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>
        
        {recentDeals.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {recentDeals.map((deal) => (
              <DealCard key={deal.id} deal={deal} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Briefcase}
            title="Pas encore de deals"
            description="Commencez par ajouter votre premier deal au pipeline."
            actionLabel="Ajouter un Deal"
            onAction={() => {}}
          />
        )}
      </section>

      {/* Implementation Notes Card */}
      <section>
        <div className="card-premium border-dashed border-2">
          <h3 className="font-semibold mb-3">Résumé de l'Implémentation Phase 1</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm">
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Entités Créées</h4>
              <ul className="space-y-1 text-foreground">
                <li>• Deal (avec Propriété, Acquisition, Rénovation, Financement, Sortie)</li>
                <li>• Scénario (Conservateur, Base, Agressif)</li>
                <li>• Partenaire (Sourceur, Chef de Projet, Architecte, Courtier)</li>
                <li>• Comparable (Données marché)</li>
                <li>• Jalon & Alerte de Risque</li>
                <li>• Hypothèses par Défaut</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Calculs Créés</h4>
              <ul className="space-y-1 text-foreground">
                <li>• Coût Total d'Acquisition</li>
                <li>• Coût au m²</li>
                <li>• Total Rénovation</li>
                <li>• Coût Total du Projet</li>
                <li>• Profit Brut & Net</li>
                <li>• ROI % & Marge Brute %</li>
                <li>• Prix de Sortie Seuil</li>
                <li>• Offre Max au ROI Cible</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-muted-foreground mb-2">Reporté à Plus Tard</h4>
              <ul className="space-y-1 text-foreground">
                <li>• Authentification utilisateur</li>
                <li>• Moteur de scoring IA</li>
                <li>• Scraping de données externes</li>
                <li>• Gestion documentaire</li>
                <li>• Alertes & notifications</li>
                <li>• Rapports avancés</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Page Help */}
      <PageHelpBlock 
        pageKey="executive-overview" 
        content={pageExplanations.executiveOverview} 
      />
    </div>
  );
};

export default ExecutiveOverview;
